import { useContext, useMemo } from 'react';
import Chart from '../../chart';
import InlineOptions from '../../inline-options';
import { WindowEnum } from '../../../../providers/volatility-cones-provider';
import './styles.scss';
import { VolatilityHeatmapContext } from '../../../../providers/widgets/volatility-heatmap/volatility-heatmap-provider';
import { periodOptions } from '../../constants/volatility-options';
import { TimestampCalendarPicker } from '../../calendar/timestamp-calendar-picker';
import { color } from 'echarts';
import usePersistentState from '../../../../providers/use-persistent-state';
import Divider from '../../divider';

const VolatilityHeatmapContent = ({ providerKey }: { providerKey: string }) => {
  const {
    token,
    selectedPeriod,
    setSelectedPeriod,
    selectedStrikeType,
    setSelectedStrikeType,
    data,
    isLoading,
    fetchVolHeatmap,
    isHeatmapLive,
    error: isHeatmapError,
  } = useContext(VolatilityHeatmapContext);

  const xCats = data?.xCategories || [];
  const yCats = data?.yCategories || [];
  const points = data?.points || [];

  const rrFlyOrder = ['ATMF', '25FLY', '25RR', '10FLY', '10RR'];

  const { xCats: orderedXCats, points: orderedPoints } = useMemo(() => {
    if (selectedStrikeType === 'RRFLY' && xCats.length) {
      const indices = rrFlyOrder.map((label) => xCats.indexOf(label)).filter((i) => i !== -1);
      const newXCats = indices.map((i) => xCats[i]);
      const xMap = new Map<number, number>();
      indices.forEach((origIdx, newIdx) => xMap.set(origIdx, newIdx));
      const newPoints = points
        .filter((p) => xMap.has(p[0]))
        .map((p) => {
          const newX = xMap.get(p[0])!;
          return [newX, p[1], p[2], p[3]] as typeof p;
        });
      return { xCats: newXCats, points: newPoints };
    }
    return { xCats, points };
  }, [selectedStrikeType, xCats, points]);

  const yCatsReversed = useMemo(() => [...yCats].reverse(), [yCats]);
  const pointsReversed = useMemo(
    () =>
      orderedPoints.map((p) => {
        const x = p[0];
        const y = p[1];
        const heat = p[2];
        const vol = p[3];
        const newY = yCats.length ? yCats.length - 1 - y : y;
        return [x, newY, heat, vol] as typeof p;
      }),
    [orderedPoints, yCats.length],
  );

  const [legendSelected, setLegendSelected] = usePersistentState<Record<string, boolean>>(
    [providerKey, 'VOL-HEATMAP'],
    'legendSelected',
    {},
  );

  const handleApply = (timestamp: string) => {
    if (!timestamp) return;

    fetchVolHeatmap({
      tokenKey: token,
      window: selectedPeriod,
      strike: selectedStrikeType,
      timestamp,
    });
  };

  const handleReset = () => {
    fetchVolHeatmap({
      tokenKey: token,
      window: selectedPeriod,
      strike: selectedStrikeType,
    });
  };

  const renderInlineOptions = () => {
    return (
      <div className="volatility-heatmap-header">
        <div className="volatility-heatmap-inline-options">
          <InlineOptions
            options={periodOptions}
            selectedOption={selectedPeriod}
            onOptionChange={(val) => setSelectedPeriod(val as WindowEnum)}
            maxVisibleOptions={5}
            leftLabel="Lookback Window"
          />
          <Divider />
          <InlineOptions
            options={[
              { label: 'PUT/CALL', value: '' },
              { label: 'RR/FLY', value: 'RRFLY' },
            ]}
            selectedOption={selectedStrikeType}
            onOptionChange={(val) => setSelectedStrikeType(val as string)}
            maxVisibleOptions={5}
            leftLabel=""
          />
        </div>
        <div className="volatility-heatmap-timestamp-calendar-picker">
          <TimestampCalendarPicker
            handleChangeDatetime={handleApply}
            isLive={isHeatmapLive}
            reset={handleReset}
            displayTimestamp={data?.asof}
            inputIsUTC
          />
        </div>
      </div>
    );
  };

  const axisLabelCommon = useMemo(() => {
    const baseSize = 12;
    const maxCats = Math.max(orderedXCats.length, yCats.length);
    const fontSize = maxCats > 12 ? 10 : maxCats > 16 ? 9 : baseSize;
    return {
      margin: 12,
      color: 'white',
      fontSize,
      fontWeight: 400,
      fontFamily: 'Inter',
    };
  }, [orderedXCats.length, yCats.length]);

  const xAxis = useMemo(
    () => ({
      type: 'category' as const,
      data: orderedXCats,
      position: 'top' as const,
      boundaryGap: true,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: { ...axisLabelCommon, interval: 0, hideOverlap: false },
      splitLine: { show: false },
      splitArea: { show: false },
      z: 10,
    }),
    [orderedXCats, axisLabelCommon],
  );

  const yAxis = useMemo(
    () => ({
      type: 'category' as const,
      data: yCatsReversed,
      position: 'left' as const,
      boundaryGap: true,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        ...axisLabelCommon,
        align: 'right' as const,
        interval: 0,
        hideOverlap: false,
      },
      splitLine: { show: false },
      splitArea: { show: false },
      z: 10,
    }),
    [yCats, axisLabelCommon],
  );

  const visualMap = useMemo(
    () => ({
      type: 'continuous' as const,
      min: 0,
      max: 1,
      dimension: 2,
      calculable: true,
      realtime: true,
      orient: 'horizontal' as const,
      left: 'center',
      bottom: 0,
      inRange: {
        color: ['#FAA638', '#52E0E0'],
        colorAlpha: 1,
      },
      outOfRange: {
        colorAlpha: 0,
      },
      text: ['1.0', '0.0'],
      textGap: 8,
      itemWidth: 16,
      itemHeight: 196,
      textStyle: {
        color: '#F2F5F7',
        rich: {
          lbl: { color: '#C7D6FF', fontWeight: 600 },
        },
      },
    }),
    [],
  );

  const series = useMemo(
    () => [
      {
        name: '',
        type: 'custom' as const,
        coordinateSystem: 'cartesian2d' as const,
        data: pointsReversed,
        encode: { x: 0, y: 1 },
        clip: true,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        renderItem: (params: any, api: any) => {
          const xIdx = api.value(0);
          const yIdx = api.value(1);
          const percentile = api.value(2);
          const vol = api.value(3);

          const [cx, cy] = api.coord([xIdx, yIdx]);
          const [cellW, cellH] = api.size([1, 1]);
          const padX = Math.min(cellW * 0.02, 3);
          const padY = Math.min(cellH * 0.06, 4);

          const x = cx - cellW / 2 + padX;
          const y = cy - cellH / 2 + padY;
          const width = Math.max(22, cellW - padX * 2);
          const height = Math.max(22, cellH - padY * 2);
          const r = Math.min(width, height) * 0.18;

          const fill = api.visual('color');
          const alpha = color.parse(fill)?.[3] ?? 1;

          const showTexts = alpha > 0;

          const children: any[] = [
            {
              type: 'rect',
              shape: { x, y, width, height, r },
              style: {
                fill,
                lineWidth: 0,
                stroke: 'transparent',
                opacity: alpha,
              },
              silent: false,
            },
          ];

          if (showTexts) {
            children.push(
              {
                type: 'text',
                style: {
                  text: `${vol.toFixed(1)}%`,
                  x: x + width / 2,
                  y: y + height / 2 - 6,
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                  fill: '#002649',
                  font: '600 12px Inter, Poppins, sans-serif',
                  opacity: alpha,
                },
                silent: true,
              },
              {
                type: 'text',
                style: {
                  text: `(${percentile.toFixed(2)})`,
                  x: x + width / 2,
                  y: y + height / 2 + 10,
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                  fill: '#002649',
                  font: '400 11px Inter, Poppins, sans-serif',
                  opacity: alpha,
                },
                silent: true,
              },
            );
          }

          return {
            type: 'group',
            children,
            silent: false,
            cursor: 'pointer',
          };
        },
        tooltip: {
          trigger: 'item',
          formatter: (p: any) => {
            const [xi, yi, pct, vol] = p.value;
            const yLbl = yCatsReversed[yi] ?? '';
            const xLbl = orderedXCats[xi] ?? '';
            return `${yLbl} • ${xLbl}<br/>Vol: <b>${Number(vol).toFixed(1)}%</b><br/>Percentile: <b>${Number(pct).toFixed(2)}</b>`;
          },
        },
      },
    ],
    [pointsReversed, orderedXCats, yCatsReversed],
  );

  const grid = useMemo(
    () => ({
      left: '16px',
      right: '16px',
      top: '10px',
      bottom: '64px',
      containLabel: true,
    }),
    [],
  );

  return (
    <div className="volatility-heatmap-container">
      {renderInlineOptions()}
      {isHeatmapError && (
        <div className="heatmap-no-data">
          <span>No Data Found.</span>
        </div>
      )}
      {!isHeatmapError && (
        <Chart
          series={series as any}
          xAxis={xAxis as any}
          yAxis={yAxis as any}
          grid={grid}
          visualMap={visualMap}
          tooltip={{}}
          isLoading={isLoading}
          className="volatility-heatmap-chart"
          legend={{
            selected: legendSelected,
          }}
          onLegendSelectChanged={(payload) => setLegendSelected(payload.selected || {})}
        />
      )}
    </div>
  );
};

export default VolatilityHeatmapContent;
